import z from 'zod';

import { reportErrorToSentry } from '#configuration/setup/sentry.ts';

import { config } from '../../config.ts';
import { getAllISO3CountryCodes } from '../country.util.ts';

const AddressSchema = z.object({
    label: z.string(),
    countryCode: z.string(),
    countryName: z.string(),
    stateCode: z.string().optional(),
    state: z.string().optional(),
    countyCode: z.string().optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    district: z.string().optional(),
    street: z.string().optional(),
    postalCode: z.string().optional(),
    houseNumber: z.string().optional(),
});

const PositionSchema = z.object({
    lat: z.number(),
    lng: z.number(),
});

const BaseHereSuggestionSchema = z.object({
    title: z.string().optional(),
    id: z.string().optional(),
    language: z.string().optional(),
    resultType: z.string().optional(),
    houseNumberType: z.string().optional(),
    access: z.array(PositionSchema).optional(),
    distance: z.number().optional(),
    address: AddressSchema.optional(),
});

const FetchHereLocationSuggestionSchema = BaseHereSuggestionSchema.extend({
    position: PositionSchema.optional(),
});

export type FetchHereSuggestion = z.infer<typeof FetchHereLocationSuggestionSchema>;

const HereSuggestionSchema = BaseHereSuggestionSchema.extend({
    position: PositionSchema,
});

export type HereSuggestion = z.infer<typeof HereSuggestionSchema>;

const isValidHereSuggestion = (suggestion: FetchHereSuggestion): suggestion is HereSuggestion => !!suggestion.position;

export const fetchHereLocationSuggestions = async (query: string): Promise<HereSuggestion[]> => {
    if (!query) {
        return [];
    }

    try {
        const searchParams = new URLSearchParams({
            q: query,
            at: '39.31974,-76.56721',
            limit: '5',
            show: 'details',
            in: `countryCode:${getAllISO3CountryCodes()}`,
            apiKey: config.hereApiKey,
        });
        const response = await fetch(
            `https://autosuggest.search.hereapi.com/v1/autosuggest?${searchParams.toString()}`
        );

        const data = await response.json();
        const suggestions = FetchHereLocationSuggestionSchema.array().safeParse(data.items);

        if (!suggestions.success) {
            console.error('[feature/location-suggestions] Error parsing location suggestions:', suggestions.error);
            reportErrorToSentry(suggestions.error);
            return [];
        }
        return suggestions.data.filter(isValidHereSuggestion);
    } catch (error) {
        console.error('[feature/location-suggestions] Error fetching location suggestions:', error);
        reportErrorToSentry(error);
        return [];
    }
};
